import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook to manage specified URL search parameters.
 * @param {string[]} managedKeys - An array of query parameter keys to manage.
 * @param {Object.<string, string>} [initialValues={}] - Optional initial values for the managed keys.
 * @returns {{params: Object.<string, string>, setParams: Function}} - The current params state and a function to update them.
 */
export const useUrlParams = (managedKeys = [], initialValues = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or initialValues
  const [params, setParamsState] = useState(() => {
    const initialState = {};
    managedKeys.forEach(key => {
      initialState[key] = searchParams.get(key) ?? initialValues[key] ?? null;
    });
    return initialState;
  });

  // Update URL when params state changes
  useEffect(() => {
    // Construct the target search params string based *only* on the current state (`params`)
    const targetSearchParams = new URLSearchParams();
    managedKeys.forEach(key => {
      const value = params[key];
      // Ensure value is treated as string; handle null/undefined/empty string consistently
      const stringValue = value ? String(value) : '';
      if (stringValue) {
        // Only add if value is not empty or null/undefined
        targetSearchParams.set(key, stringValue);
      }
    });
    targetSearchParams.sort(); // Ensure consistent order for comparison

    // Get the current search params string, but only considering managed keys
    const currentManagedSearchParams = new URLSearchParams();
    managedKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        // Check if the key exists and has a value in the URL
        currentManagedSearchParams.set(key, value);
      }
    });
    currentManagedSearchParams.sort(); // Ensure consistent order for comparison

    const targetString = targetSearchParams.toString();
    const currentString = currentManagedSearchParams.toString();

    // Only update the URL if the string representation of managed params differs
    if (targetString !== currentString) {
      // Create the final new search params, preserving unmanaged keys from the current URL
      const finalSearchParams = new URLSearchParams(searchParams);
      managedKeys.forEach(key => {
        const value = params[key];
        const stringValue = value ? String(value) : '';
        if (stringValue) {
          finalSearchParams.set(key, stringValue);
        } else {
          finalSearchParams.delete(key); // Remove managed key if not in state or empty
        }
      });
      setSearchParams(finalSearchParams, { replace: true });
    }
    // Dependencies: React to changes in internal state (`params`) and external URL (`searchParams`).
  }, [params, searchParams, managedKeys, setSearchParams]);

  // Memoized setter function to update the state
  const setParams = useCallback(
    newParams => {
      // Accepts an object of key-value pairs to update
      setParamsState(prevParams => {
        const updated = { ...prevParams };
        let hasChanged = false;
        for (const key in newParams) {
          if (managedKeys.includes(key) && prevParams[key] !== newParams[key]) {
            updated[key] = newParams[key];
            hasChanged = true;
          }
        }
        // Only return a new object reference if something actually changed
        return hasChanged ? updated : prevParams;
      });
    },
    [managedKeys]
  );

  return { params, setParams };
};
